"use client"

import { useState } from "react"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"
import {
  clicksFromCpc,
  cpcFromCpmCtr,
  cpcFromSpend,
  spendFromCpc,
} from "./cpc-math"
import { SolveForWidget, type SolveForField } from "./solve-for-widget"

/**
 * CPC = spend ÷ clicks — solve for any one of the three — plus a second card
 * that derives the effective CPC of impression-billed buying from CPM and CTR.
 *
 * Defaults are arithmetic, not benchmarks: $1,800 for 1,500 clicks = $1.20,
 * and an $18 CPM at a 1.2% CTR = $1.50 per click.
 */

type CpcKey = "cpc" | "spend" | "clicks"

const formatCount = (n: number) =>
  Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—"

/** Clicks are bought whole, so a click goal rounds down, not to nearest. */
const formatClicksBought = (n: number) =>
  Number.isFinite(n) ? Math.floor(n).toLocaleString("en-US") : "—"

const formatPct = (pct: number) =>
  `${pct.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`

const fields = [
  {
    key: "cpc",
    tabLabel: "CPC",
    label: "Cost per click",
    resultLabel: "Cost per click",
    defaultValue: "1.2",
    prefix: "$",
    format: (n) => formatCurrency(n),
  },
  {
    key: "spend",
    tabLabel: "Spend",
    label: "Ad spend",
    resultLabel: "Budget needed",
    defaultValue: "1800",
    prefix: "$",
    format: (n) => formatCurrency(n),
  },
  {
    key: "clicks",
    tabLabel: "Clicks",
    label: "Clicks",
    resultLabel: "Clicks the budget buys",
    defaultValue: "1500",
    step: 1,
    format: formatClicksBought,
  },
] as const satisfies readonly [
  SolveForField<CpcKey>,
  SolveForField<CpcKey>,
  SolveForField<CpcKey>,
]

function CpcFromCpmCard() {
  const [cpm, setCpm] = useState("18")
  const [ctr, setCtr] = useState("1.2")

  const cpmN = parseNum(cpm)
  const ctrN = parseNum(ctr)
  const cpc =
    cpmN === null || ctrN === null ? NaN : cpcFromCpmCtr(cpmN, ctrN)
  const ok = Number.isFinite(cpc)
  const clicksPerThousand = ok && ctrN !== null ? ctrN * 10 : NaN

  return (
    <CalculatorCard title="CPC from CPM and CTR">
      <p className="-mt-3 mb-6 text-sm text-muted-foreground">
        Most social and video campaigns are billed per impression, so the cost of a
        click is set by two things: what the impressions cost and how often they are
        clicked.
      </p>
      <CalculatorGrid>
        <NumberField
          id="cpc-derive-cpm"
          label="CPM (cost per 1,000 impressions)"
          value={cpm}
          onChange={setCpm}
          prefix="$"
        />
        <NumberField
          id="cpc-derive-ctr"
          label="Click-through rate"
          value={ctr}
          onChange={setCtr}
          suffix="%"
        />
      </CalculatorGrid>
      <div className="mt-6">
        <ResultStat
          label="Effective cost per click"
          value={ok ? formatCurrency(cpc) : "—"}
          caption={
            ok && cpmN !== null && ctrN !== null
              ? `Every 1,000 impressions cost ${formatCurrency(cpmN)} and return about ${clicksPerThousand.toLocaleString("en-US", { maximumFractionDigits: 1 })} clicks at a ${formatPct(ctrN)} CTR. Halve the CTR and the same CPM buys clicks at twice the price.`
              : "Enter a CPM and a CTR between 0 and 100% (above zero)."
          }
        />
      </div>
      <FormulaNote>
        CPC = CPM ÷ (1,000 × CTR), with CTR as a fraction — or CPM ÷ (10 × CTR%).
        Use the CTR that matches the click you pay for: link CTR for a link-click
        CPC.
      </FormulaNote>
    </CalculatorCard>
  )
}

export function CpcWidget() {
  return (
    <div className="space-y-6">
      <SolveForWidget<CpcKey>
        title="CPC Calculator"
        idPrefix="cpc"
        fields={fields}
        solve={(target, { cpc, spend, clicks }) => {
          if (target === "cpc") {
            if (spend === null || clicks === null) return null
            const value = cpcFromSpend(spend, clicks)
            if (!Number.isFinite(value)) return null
            return {
              value,
              caption:
                value > 0
                  ? `${formatCurrency(spend)} over ${formatCount(clicks)} clicks. Every $100 of this budget bought about ${(100 / value).toLocaleString("en-US", { maximumFractionDigits: 1 })} clicks.`
                  : `${formatCount(clicks)} clicks at no cost — nothing was spent, so there is no cost per click to compare.`,
            }
          }
          if (target === "spend") {
            if (cpc === null || clicks === null) return null
            const value = spendFromCpc(cpc, clicks)
            if (!Number.isFinite(value)) return null
            return {
              value,
              caption: `${formatCount(clicks)} clicks at ${formatCurrency(cpc)} each. If the click price rises 20% mid-flight, the same goal needs ${formatCurrency(value * 1.2)}.`,
            }
          }
          if (spend === null || cpc === null) return null
          const value = clicksFromCpc(spend, cpc)
          if (!Number.isFinite(value)) return null
          return {
            value,
            caption: `${formatCurrency(spend)} at an average ${formatCurrency(cpc)} per click. Your analytics will usually record fewer sessions than the platform records clicks.`,
          }
        }}
        invalidCaption={(target) =>
          target === "cpc"
            ? "Enter ad spend and a click count above zero."
            : target === "spend"
              ? "Enter a cost per click and a click goal."
              : "Enter a budget and a cost per click above zero."
        }
        formulaNote={(target) =>
          target === "cpc"
            ? "CPC = Ad spend ÷ Clicks. Take spend and clicks from the same campaign, date range and click definition — link clicks if that is what the campaign is judged on."
            : target === "spend"
              ? "Ad spend = CPC × Clicks."
              : "Clicks = Ad spend ÷ CPC."
        }
      />
      <CpcFromCpmCard />
    </div>
  )
}
